import reactionDefaults from "./reaction-defaults.json";

// Generic emoji-reaction primitives, shared across features (chat messages,
// activitystream posts). The host feature maps its own member/serialization
// shape onto ReactionArg and supplies i18n labels, so this module stays
// namespace-agnostic.

// One reaction (one entry per reacting member).
export type ReactionMember = {
  id: string;
  name: string;
  imageUrl?: string;
  deleted?: boolean;
};

export type ReactionArg = {
  emoji: string;
  member: ReactionMember;
};

// Built-in default quick-bar and picker emoji sets. The single source of
// truth is reaction-defaults.json, imported here for the frontend and read
// server-side by Activity::ActivityReaction (so the SAD can pre-fill them) —
// keeping Ruby, Elixir-fed config and the React pickers from ever diverging.
// Used whenever a network has not configured its own set via the SAD.
export const DEFAULT_QUICK_REACTIONS: string[] =
  reactionDefaults.quick_reactions;
export const DEFAULT_PICKER_EMOJIS: string[] = reactionDefaults.emoji_list;

// The emoji that classic "likes" map to. Kept in sync with the backend
// ActivityReactionConverter::LIKE_EMOJI so likes <-> 👍 round-trip cleanly.
export const LIKE_EMOJI = "👍";

// Resolve the quick-bar emojis for the current network. Read at render time
// from the (non-reactive) Preload bootstrap, so changes apply on next page load.
//
// Precedence: an explicitly configured quick-bar set wins. Otherwise, if the
// network restricted the picker (emoji_list) we fall back to the head of that
// picker — never the global default, which could offer emojis the picker (and
// thus the server allowlist) disallows. Only an unrestricted network uses the
// built-in default quick-bar.
export function resolveQuickReactions(): string[] {
  const config = Preload.current_network?.config?.activities;
  const configured = config?.reactions_quick_reactions;
  if (configured?.length) return configured;

  const picker = config?.reactions_emoji_list;
  if (picker?.length) return picker.slice(0, DEFAULT_QUICK_REACTIONS.length);

  return DEFAULT_QUICK_REACTIONS;
}

// Resolve the full emoji-picker set for the current network: its configured set
// if non-empty, otherwise the built-in default.
export function resolvePickerEmojis(): string[] {
  const configured =
    Preload.current_network?.config?.activities?.reactions_emoji_list;
  return configured?.length ? configured : DEFAULT_PICKER_EMOJIS;
}

// i18n labels supplied by the host feature.
export type ReactionLabels = {
  addReaction: string;
  pickEmoji: string;
  reactedBy: string;
  remove: string;
};

export type AggregatedReaction = {
  emoji: string;
  count: number;
  members: ReactionMember[];
  mine: boolean;
};

// Group a flat reactions list by emoji, preserving first-seen order, counting
// each emoji and flagging the current user's own reaction.
export function aggregateReactions(
  reactions: ReactionArg[],
  myId: string,
): AggregatedReaction[] {
  const order: string[] = [];
  const byEmoji: Record<string, AggregatedReaction> = {};

  for (const { emoji, member } of reactions) {
    if (!emoji || !member) continue;

    if (!byEmoji[emoji]) {
      byEmoji[emoji] = { emoji, count: 0, members: [], mine: false };
      order.push(emoji);
    }

    const aggregated = byEmoji[emoji];
    aggregated.count += 1;
    aggregated.members.push(member);
    if (member.id === myId) aggregated.mine = true;
  }

  return order.map((emoji) => byEmoji[emoji]);
}

// The current user's own reaction emoji, if any (one per user).
export function myReactionEmoji(
  reactions: ReactionArg[] | undefined,
  myId: string,
): string | undefined {
  return reactions?.find((reaction) => reaction.member?.id === myId)?.emoji;
}
